import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxOpen, faSpinner, faTruck, faUser } from '@fortawesome/free-solid-svg-icons'
import type { AssetOrInventory } from './types'
import { formatJobLabel } from './types'

type TallyItemListProps = {
  items: AssetOrInventory[]
  loading: boolean
  error: string | null
  isAssetTab: boolean
  collapsedView: boolean
  getAssignmentLabel: (item: AssetOrInventory) => string | null
  onOpen: (item: AssetOrInventory) => void
  selectionEnabled?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: (selectAll: boolean) => void
}

export const TallyItemList: React.FC<TallyItemListProps> = ({
  items,
  loading,
  error,
  isAssetTab,
  collapsedView,
  getAssignmentLabel,
  onOpen,
  selectionEnabled = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="text-3xl text-primary animate-spin" />
        <span className="ml-3 text-base-content/70">
          {isAssetTab ? 'Loading assets...' : 'Loading inventory...'}
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-box border border-error/30 bg-error/10 px-4 py-8 text-center text-error">
        {error}
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-box border border-dashed border-base-300 bg-base-200/40 px-4 py-16 text-center">
        <FontAwesomeIcon
          icon={isAssetTab ? faTruck : faBoxOpen}
          className="text-3xl text-base-content/35"
        />
        <div>
          <p className="font-medium">{isAssetTab ? 'No assets found' : 'No inventory found'}</p>
          <p className="text-sm text-base-content/60 mt-1">
            {isAssetTab
              ? 'Add an asset to get started.'
              : 'Add a pallet or scan a barcode to get started.'}
          </p>
        </div>
      </div>
    )
  }

  const selectedCount = items.filter((item) => selectedIds?.has(item._id)).length
  const allSelected = selectionEnabled && items.length > 0 && selectedCount === items.length
  const someSelected = selectionEnabled && selectedCount > 0 && selectedCount < items.length

  if (collapsedView) {
    return (
      <div className="overflow-x-auto rounded-box border border-base-200">
        <table className="table table-zebra table-sm md:table-md w-full">
          <thead className="bg-base-200/80 sticky top-0 z-10">
            <tr>
              {selectionEnabled ? (
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected
                    }}
                    onChange={(e) => onToggleSelectAll?.(e.target.checked)}
                    aria-label="Select all"
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ) : null}
              <th>Name</th>
              {isAssetTab ? (
                <>
                  <th>Type</th>
                  <th>Assigned</th>
                  <th>Status</th>
                  <th className="hidden lg:table-cell">Location</th>
                </>
              ) : (
                <>
                  <th>Location</th>
                  <th>Qty</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const job = formatJobLabel(item)
              const assignment = getAssignmentLabel(item)
              const checked = Boolean(selectedIds?.has(item._id))
              return (
                <tr
                  key={item._id}
                  className={`cursor-pointer hover:bg-base-200/60 ${checked ? 'bg-primary/5' : ''}`}
                  onClick={() => onOpen(item)}
                >
                  {selectionEnabled ? (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={checked}
                        onChange={() => onToggleSelect?.(item._id)}
                        aria-label={`Select ${item.name}`}
                      />
                    </td>
                  ) : null}
                  <td className="font-medium">{item.name}</td>
                  {isAssetTab ? (
                    <>
                      <td>{item.type}</td>
                      <td className="max-w-[12rem] truncate">{assignment || '—'}</td>
                      <td>
                        {item.status ? (
                          <span className="badge badge-ghost badge-sm">{item.status}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="hidden lg:table-cell max-w-[14rem] truncate">
                        {job || item.location?.address || '—'}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="max-w-[16rem] truncate">{job || '—'}</td>
                      <td>{item.quantity ?? '—'}</td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {items.map((item) => {
        const job = formatJobLabel(item)
        const assignment = getAssignmentLabel(item)
        return (
          <button
            key={item._id}
            type="button"
            className="card bg-base-100 border border-base-200 text-left shadow-sm hover:shadow-md hover:border-primary/30 transition-all"
            onClick={() => onOpen(item)}
          >
            <div className="card-body gap-2 p-4">
              <h4 className="card-title text-base leading-snug">{item.name}</h4>
              {isAssetTab ? (
                <div className="flex flex-wrap gap-1.5 text-xs">
                  {item.status ? (
                    <span className="badge badge-outline badge-sm">{item.status}</span>
                  ) : null}
                  {item.type ? <span className="badge badge-ghost">{item.type}</span> : null}
                  {job ? <span className="badge badge-primary badge-outline">{job}</span> : null}
                  {!job && assignment ? (
                    <span className="badge badge-primary badge-outline">{assignment}</span>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 text-xs">
                  {job ? <span className="badge badge-primary badge-outline">{job}</span> : null}
                  {assignment ? (
                    <span className="badge badge-ghost gap-1">
                      <FontAwesomeIcon icon={faUser} />
                      {assignment}
                    </span>
                  ) : null}
                  <span className="badge badge-ghost">Qty {item.quantity ?? 0}</span>
                </div>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default TallyItemList
